import type Delta from "quill-delta";
import { BULLETED_RADIUS_RATIO, MAX_SYMBOLS_IN_LINE } from "../constantsV2";
import type {
    BlockAttrs,
    InlineAttrs,
    TextLine,
    TextSegment,
    TextToken,
    TextV2Data,
} from "../typesV2";
import {
    calculateLineWidths,
    getBaseFont,
    getSegmentFont,
    measureTextWidth,
    setCanvasFont,
} from "./measurements";

const isWhitespaceChar = (value: string): boolean => {
    return /\s/.test(value);
};

export const cloneLines = (lines: TextLine[]): TextLine[] => {
    return lines.map((line) => ({
        segments: line.segments.map((segment) => ({
            text: segment.text,
            color: segment.color,
            bold: segment.bold,
            italic: segment.italic,
            strike: segment.strike,
            underline: segment.underline,
            link: segment.link,
        })),
        listType: line.listType,
        listNumber: line.listNumber,
    }));
};

/**
 * Находит позицию последнего пробела в сегментах до указанного количества символов.
 * Возвращает segmentIndex, charIndex - где резать.
 */
const findLastSpaceBefore = (
    segments: TextSegment[],
    maxChars: number,
): { segmentIndex: number; charIndex: number } | null => {
    let charCount = 0;
    let lastSpaceSegmentIndex = -1;
    let lastSpaceCharIndex = -1;

    for (let segIdx = 0; segIdx < segments.length; segIdx++) {
        const seg = segments[segIdx];
        const segLength = seg.text.length;

        if (charCount + segLength >= maxChars) {
            // Ищем пробел в текущем сегменте до maxChars
            const remaining = Math.min(Math.max(0, maxChars - charCount), segLength);
            for (let i = remaining - 1; i >= 0; i--) {
                if (isWhitespaceChar(seg.text[i])) {
                    return { segmentIndex: segIdx, charIndex: i + 1 };
                }
            }
            // Если пробел не найден в текущем сегменте, используем последний найденный
            if (lastSpaceSegmentIndex >= 0) {
                return {
                    segmentIndex: lastSpaceSegmentIndex,
                    charIndex: lastSpaceCharIndex + 1,
                };
            }
            // Если пробелов вообще нет, режем ровно по лимиту (минимум 1 символ)
            const safeCharIndex = Math.min(Math.max(1, remaining), segLength);
            return { segmentIndex: segIdx, charIndex: safeCharIndex };
        }

        // Ищем пробел в текущем сегменте
        for (let i = segLength - 1; i >= 0; i--) {
            if (isWhitespaceChar(seg.text[i])) {
                lastSpaceSegmentIndex = segIdx;
                lastSpaceCharIndex = i;
                break;
            }
        }

        charCount += segLength;
    }

    return null; // Вся строка помещается
};

/**
 * Считает общее количество символов в сегментах.
 */
const countChars = (segments: TextSegment[]): number => {
    return segments.reduce((sum, seg) => sum + seg.text.length, 0);
};

/**
 * Разрезает сегменты на две части по указанной позиции.
 */
const splitSegmentsAt = (
    segments: TextSegment[],
    segmentIndex: number,
    charIndex: number,
): { head: TextSegment[]; tail: TextSegment[] } => {
    const head: TextSegment[] = [];
    const tail: TextSegment[] = [];

    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];

        if (i < segmentIndex) {
            // Полностью в head
            head.push(seg);
        } else if (i > segmentIndex) {
            // Полностью в tail
            tail.push(seg);
        } else {
            // Разрезаем текущий сегмент
            const headText = seg.text.substring(0, charIndex);
            const tailText = seg.text.substring(charIndex);

            if (headText.length > 0) {
                head.push({
                    text: headText,
                    color: seg.color,
                    bold: seg.bold,
                    italic: seg.italic,
                    strike: seg.strike,
                    underline: seg.underline,
                    link: seg.link,
                });
            }

            if (tailText.length > 0) {
                tail.push({
                    text: tailText,
                    color: seg.color,
                    bold: seg.bold,
                    italic: seg.italic,
                    strike: seg.strike,
                    underline: seg.underline,
                    link: seg.link,
                });
            }
        }
    }

    return { head, tail };
};

/**
 * Переносит длинные строки текста на несколько строк по ограничению длины (85 символов).
 * Алгоритм:
 * - Если строка длиннее MAX_SYMBOLS_IN_LINE (85 символов), разбивает её на части
 * - Старается переносить по пробелу (чтобы не разрывать слова)
 * - Если пробелов нет, режет ровно по лимиту символов
 * - Сохраняет все стили сегментов при разрезе
 * - Для списков (bullet/number) маркер остаётся только на первой строке элемента
 */
export const wrapLinesByMaxSymbols = (lines: TextLine[]): TextLine[] => {
    const result: TextLine[] = [];

    for (const line of lines) {
        if (line.segments.length === 0) {
            result.push({ ...line, segments: [] });
            continue;
        }

        let remainingSegments = line.segments;
        let isFirst = true;

        while (countChars(remainingSegments) > MAX_SYMBOLS_IN_LINE) {
            const splitPos = findLastSpaceBefore(remainingSegments, MAX_SYMBOLS_IN_LINE);

            if (!splitPos) {
                break;
            }

            const { head, tail } = splitSegmentsAt(
                remainingSegments,
                splitPos.segmentIndex,
                splitPos.charIndex,
            );

            result.push({
                segments: head,
                listType: isFirst ? (line.listType ?? null) : null,
                listNumber: isFirst ? line.listNumber : undefined,
            });

            remainingSegments = tail;
            isFirst = false;
        }

        result.push({
            segments: remainingSegments,
            listType: isFirst ? (line.listType ?? null) : null,
            listNumber: isFirst ? line.listNumber : undefined,
        });
    }

    return result;
};

/**
 * Находит позицию последнего пробела в сегментах до указанной ширины в пикселях.
 * Возвращает segmentIndex, charIndex - где резать.
 */
const isSpaceToken = (value: string): boolean => {
    return /^\s+$/.test(value);
};

const isSameStyle = (a: TextSegment, b: TextToken): boolean => {
    return (
        a.color === b.color &&
        a.bold === b.bold &&
        a.italic === b.italic &&
        a.strike === b.strike &&
        a.underline === b.underline &&
        a.link === b.link
    );
};

const appendTokenToSegments = (segments: TextSegment[], token: TextToken): void => {
    if (token.text.length === 0) {
        return;
    }
    const last = segments[segments.length - 1];
    if (last && isSameStyle(last, token)) {
        last.text += token.text;
        return;
    }
    segments.push({
        text: token.text,
        color: token.color,
        bold: token.bold,
        italic: token.italic,
        strike: token.strike,
        underline: token.underline,
        link: token.link,
    });
};

const buildLineSegmentsFromTokens = (
    tokens: TextToken[],
    start: number,
    end: number,
): TextSegment[] => {
    const segments: TextSegment[] = [];
    for (let i = start; i < end; i++) {
        appendTokenToSegments(segments, tokens[i]);
    }
    return segments;
};

const tokenizeLine = (line: TextLine, data: TextV2Data): TextToken[] => {
    const tokens: TextToken[] = [];
    for (const segment of line.segments) {
        const font = getSegmentFont(segment, data);
        setCanvasFont(font);
        const parts = segment.text.split(/(\s+)/);
        for (const part of parts) {
            if (part.length === 0) {
                continue;
            }
            const width = measureTextWidth(part, font, true);
            tokens.push({
                text: part,
                width,
                color: segment.color,
                bold: segment.bold,
                italic: segment.italic,
                strike: segment.strike,
                underline: segment.underline,
                link: segment.link,
                isSpace: isSpaceToken(part),
            });
        }
    }
    return tokens;
};

const getTokenFont = (token: TextToken, data: TextV2Data): string => {
    const fakeSegment: TextSegment = {
        text: token.text,
        color: token.color,
        bold: token.bold,
        italic: token.italic,
        strike: token.strike,
    };
    return getSegmentFont(fakeSegment, data);
};

const splitTokenByWidth = (
    token: TextToken,
    availableWidth: number,
    data: TextV2Data,
): { headText: string; tailText: string; headWidth: number; tailWidth: number } => {
    if (availableWidth <= 0) {
        return { headText: "", tailText: token.text, headWidth: 0, tailWidth: token.width };
    }
    const font = getTokenFont(token, data);
    setCanvasFont(font);
    let low = 0;
    let high = token.text.length;
    let lastWidth = 0;
    while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        const width = measureTextWidth(token.text.slice(0, mid), font, true);
        if (width <= availableWidth) {
            low = mid;
            lastWidth = width;
        } else {
            high = mid - 1;
        }
    }

    if (low === 0 && token.text.length > 0) {
        const headText = token.text.slice(0, 1);
        const headWidth = measureTextWidth(headText, font, true);
        const tailText = token.text.slice(1);
        const tailWidth = tailText.length > 0 ? measureTextWidth(tailText, font, true) : 0;
        return { headText, tailText, headWidth, tailWidth };
    }

    const headText = token.text.slice(0, low);
    const tailText = token.text.slice(low);
    const headWidth = lastWidth;
    const tailWidth = tailText.length > 0 ? measureTextWidth(tailText, font, true) : 0;

    return { headText, tailText, headWidth, tailWidth };
};

/**
 * Переносит длинные строки текста на несколько строк по ограничению ширины в пикселях.
 * Алгоритм:
 * - Если строка шире maxWidth, разбивает её на части
 * - Старается переносить по пробелу (чтобы не разрывать слова)
 * - Если пробелов нет, режет по ширине
 * - Сохраняет все стили сегментов при разрезе
 * - Для списков (bullet/number) маркер остаётся только на первой строке элемента
 */
export const wrapLinesToWidth = (
    lines: TextLine[],
    maxWidth: number,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): TextLine[] => {
    const result: TextLine[] = [];

    for (const line of lines) {
        if (line.segments.length === 0) {
            result.push({ ...line, segments: [] });
            continue;
        }

        // Вычисляем доступную ширину для текста (без отступов и маркеров)
        let availableWidth = maxWidth - 2 * horizontalPadding;
        if (line.listType === "bullet") {
            const bulletedRadius = lineHeight * BULLETED_RADIUS_RATIO;
            availableWidth -= 2 * bulletedRadius + horizontalPadding;
        } else if (line.listType === "ordered") {
            const baseFont = getBaseFont(data);
            setCanvasFont(baseFont);
            const numberWidth = measureTextWidth(`${line.listNumber ?? 1}.`, baseFont, true);
            availableWidth -= numberWidth + horizontalPadding;
        }
        if (availableWidth < 1) {
            availableWidth = 1;
        }

        const tokens = tokenizeLine(line, data);
        let index = 0;
        let isFirst = true;

        while (index < tokens.length) {
            const lineStart = index;
            let lineWidth = 0;
            let lastSpaceIndex = -1;

            while (index < tokens.length) {
                const token = tokens[index];
                if (index === lineStart && token.width > availableWidth) {
                    break;
                }
                const fits = lineWidth + token.width <= availableWidth;
                if (!fits) {
                    break;
                }
                lineWidth += token.width;
                if (token.isSpace) {
                    lastSpaceIndex = index;
                }
                index += 1;
            }

            if (index >= tokens.length) {
                const segments = buildLineSegmentsFromTokens(tokens, lineStart, index);
                result.push({
                    segments,
                    listType: isFirst ? (line.listType ?? null) : null,
                    listNumber: isFirst ? line.listNumber : undefined,
                });
                break;
            }

            if (lastSpaceIndex >= lineStart) {
                const segments = buildLineSegmentsFromTokens(tokens, lineStart, lastSpaceIndex + 1);
                result.push({
                    segments,
                    listType: isFirst ? (line.listType ?? null) : null,
                    listNumber: isFirst ? line.listNumber : undefined,
                });
                index = lastSpaceIndex + 1;
                while (index < tokens.length && tokens[index].isSpace) {
                    index += 1;
                }
                isFirst = false;
                continue;
            }

            const available =
                lineWidth === 0 ? availableWidth : Math.max(0, availableWidth - lineWidth);
            if (available <= 0) {
                const segments = buildLineSegmentsFromTokens(tokens, lineStart, index);
                result.push({
                    segments,
                    listType: isFirst ? (line.listType ?? null) : null,
                    listNumber: isFirst ? line.listNumber : undefined,
                });
                isFirst = false;
                continue;
            }

            const token = tokens[index];
            const split = splitTokenByWidth(token, available, data);
            if (split.headText.length === 0) {
                const segments = buildLineSegmentsFromTokens(tokens, lineStart, index);
                result.push({
                    segments,
                    listType: isFirst ? (line.listType ?? null) : null,
                    listNumber: isFirst ? line.listNumber : undefined,
                });
                isFirst = false;
                continue;
            }

            const segments = buildLineSegmentsFromTokens(tokens, lineStart, index);
            appendTokenToSegments(segments, {
                ...token,
                text: split.headText,
                width: split.headWidth,
                isSpace: false,
            });
            result.push({
                segments,
                listType: isFirst ? (line.listType ?? null) : null,
                listNumber: isFirst ? line.listNumber : undefined,
            });

            tokens[index] = {
                ...token,
                text: split.tailText,
                width: split.tailWidth,
                isSpace: false,
            };
            if (tokens[index].text.length === 0) {
                index += 1;
            }
            isFirst = false;
        }
    }

    return result;
};

export const reflowTextLines = (
    originalLines: TextLine[],
    hardWidth: number,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): { lines: TextLine[]; lineWidths: number[] } => {
    const lines = wrapLinesToWidth(originalLines, hardWidth, data, horizontalPadding, lineHeight);
    const lineWidths = calculateLineWidths(lines, data, horizontalPadding, lineHeight);
    return { lines, lineWidths };
};

function hasSameStyle(segment: TextSegment, attrs: InlineAttrs, defaultColor: string) {
    return (
        segment.color === (attrs.color ?? defaultColor) &&
        segment.bold === !!attrs.bold &&
        segment.italic === !!attrs.italic &&
        segment.strike === !!attrs.strike &&
        segment.underline === !!attrs.underline &&
        segment.link === (typeof attrs.link === "string" ? attrs.link : undefined)
    );
}

function pushText(segments: TextSegment[], text: string, attrs: InlineAttrs, defaultColor: string) {
    // Чтобы не плодить сегменты: склеиваем одинаковые стили
    const last = segments[segments.length - 1];
    if (last && hasSameStyle(last, attrs, defaultColor)) {
        last.text += text;
        return segments;
    }

    const segment: TextSegment = {
        text,
        color: attrs.color ?? defaultColor,
        bold: !!attrs.bold,
        italic: !!attrs.italic,
        strike: !!attrs.strike,
        underline: !!attrs.underline,
        link: typeof attrs.link === "string" ? attrs.link : undefined,
    };

    segments.push(segment);
}

export function deltaToLines(delta: Delta, opts?: { defaultColor?: string }): TextLine[] {
    const defaultColor = opts?.defaultColor ?? "#2d2d2d";

    const out: TextLine[] = [];
    let curSegments: TextSegment[] = [];

    // block attrs применяются к строке, которая заканчивается текущим \n
    // поэтому мы читаем их именно на момент встречи \n
    const pushLine = (block?: BlockAttrs) => {
        out.push({
            segments: curSegments,
            listType: block?.list ?? null,
            indent: block?.indent ?? 0,
            align: block?.align ?? "left",
        });
        curSegments = [];
    };

    const ops = delta.ops;
    if (!ops?.length) {
        return [{ segments: [], listType: null, indent: 0 }];
    }

    for (const op of ops) {
        const ins = op.insert;
        const attrs = (op.attributes ?? {}) as InlineAttrs & BlockAttrs;

        if (typeof ins !== "string") {
            continue;
        }

        // ВАЖНО: один op может содержать несколько \n
        let start = 0;
        for (let i = 0; i < ins.length; i++) {
            if (ins[i] !== "\n") continue;

            const chunk = ins.slice(start, i); // текст до \n
            if (chunk.length > 0) {
                pushText(curSegments, chunk, attrs, defaultColor);
            }

            // \n закрывает строку, и именно на нем лежат block-атрибуты
            pushLine({
                align: attrs.align,
                list: attrs.list,
                indent: attrs.indent,
            });

            start = i + 1;
        }

        // хвост после последнего \n
        const tail = ins.slice(start);
        if (tail.length > 0) {
            pushText(curSegments, tail, attrs, defaultColor);
        }
    }

    // Если delta не закончилась \n — всё равно закрываем последнюю строку
    if (curSegments.length > 0 || out.length === 0) {
        pushLine(undefined);
    }

    return out;
}
